import type { ConsentCategory, ScriptConfig } from '../types';

/**
 * Google Consent Mode v2 bridge. Category ids are mapped to Google's signals so
 * gtag/GTM tags throttle themselves even before our own gating runs.
 */
const SIGNALS: Record<string, string[]> = {
  analytics: ['analytics_storage'],
  marketing: ['ad_storage', 'ad_user_data', 'ad_personalization'],
  functional: ['functionality_storage', 'personalization_storage'],
};

const ALL_SIGNALS = Object.values(SIGNALS).flat();

function gtag(...args: unknown[]): void {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(args);
}

function signalsFor(categories: ConsentCategory[], granted: Record<string, boolean>): Record<string, string> {
  const payload: Record<string, string> = {};

  for (const signal of ALL_SIGNALS) {
    payload[signal] = 'denied';
  }

  for (const category of categories) {
    const signals = SIGNALS[category.id];
    if (!signals) continue;
    const value = category.required || granted[category.id] ? 'granted' : 'denied';
    for (const signal of signals) {
      payload[signal] = value;
    }
  }

  // Security storage is never optional.
  payload.security_storage = 'granted';

  return payload;
}

/** Called before any tag loads, so Google starts in the denied state. */
export function pushConsentDefault(categories: ConsentCategory[], granted: Record<string, boolean>): void {
  gtag('consent', 'default', { ...signalsFor(categories, granted), wait_for_update: 500 });
}

export function pushConsentUpdate(categories: ConsentCategory[], granted: Record<string, boolean>): void {
  gtag('consent', 'update', signalsFor(categories, granted));
}

/** True when an enabled script is Google Analytics, Tag Manager, or gtag. */
export function scriptsNeedGoogleConsentMode(scripts: ScriptConfig[]): boolean {
  return scripts.some((script) => {
    if (!script.enabled) return false;
    const haystack = `${script.name || ''} ${script.content || ''}`.toLowerCase();
    return (
      haystack.includes('googletagmanager') ||
      haystack.includes('gtag(') ||
      haystack.includes('google analytics') ||
      haystack.includes('google tag manager') ||
      /\bgtm-[a-z0-9]+\b/i.test(haystack) ||
      /\bg-[a-z0-9]+\b/i.test(haystack)
    );
  });
}
